import { not, eq, and } from 'tio-ui/utilities';
import Alert from 'tio-ui/components/alert';
import { Button } from 'tio-ui/components/buttons';
import { dropTask } from 'ember-concurrency';
import { fn, hash, concat } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { Header, Section, VStack, HStack } from 'tio-ui/components/layout';
import { htmlSafe } from '@ember/template';
import { Input, Checkbox } from 'tio-ui/components/forms';
import Select from 'tio-reimagined/components/select';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { PaymentValidationErrorKey } from 'tio-employee/services/employee';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import AppAlertNoLinkedLoans from 'tio-employee/components/app/alert/no-linked-loans';
import ChangeActiveAccountModal from 'tio-common/components/account/change-active-account-modal';
import Component from '@glimmer/component';
import DefinitionList from 'tio-common/components/ui/definition-list';
import ENV from 'tio-employee/config/environment';
import RouterService from '@ember/routing/router-service';
import getOneYearAgoDate from 'tio-common/utils/get-one-year-ago-date';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import StatusText from 'tio-employee/components/slr/status-text';
import Table from 'tio-employee/components/slr/table';
import TioDownloadButton from 'tio-common/components/tio/download-button';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type EmployeeModel from 'tio-common/models/employee';
import type EmployeeService from 'tio-employee/services/employee';
import type FeaturesService from 'tio-common/services/features';
import type IntlService from 'ember-intl/services/intl';
import type PersonModel from 'tio-common/models/person';
import type PostalAddressModel from 'tio-common/models/postal-address';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import ReimaginedContributionHistory from 'tio-employee/components/reimagined/production/contribution-history';
import type { ReimaginedContributionHistoryModel } from 'tio-employee/components/reimagined/production/contribution-history';
import type ReimaginedViewService from 'tio-employee/services/reimagined-view';

interface SlrDashboardSignature {
  Args: {
    model: PersonModel | { person: PersonModel; reimagined: ReimaginedContributionHistoryModel };
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service declare employee: EmployeeService;
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare reimaginedView: ReimaginedViewService;

  @tracked isOpenAccountModal = false;
  @tracked isGeneratePdfModalOpen = false;
  @tracked startDate = getOneYearAgoDate();
  @tracked endDate = new Date().toISOString().split('T')[0];
  @tracked pdfUrl = '';
  @tracked pdfFilename = '';

  get useReimaginedAccountActivity() {
    return this.reimaginedView.isEnabled;
  }

  get reimaginedModel(): ReimaginedContributionHistoryModel | undefined {
    if (this.useReimaginedAccountActivity && 'reimagined' in this.args.model) {
      return (this.args.model as { reimagined: ReimaginedContributionHistoryModel }).reimagined;
    }
    return undefined;
  }

  get person(): PersonModel {
    if ('person' in this.args.model) {
      return (this.args.model as { person: PersonModel }).person;
    }
    return this.args.model as PersonModel;
  }

  get lastPaymentAmount() {
    return (this.employee.contributionPayments[0]?.amount || 0) / 100;
  }

  get lastPaymentDate() {
    return (
      this.employee.contributionPayments[0]?.transaction_date ||
      this.employee.contributionPayments[0]?.line_item_period ||
      ''
    );
  }

  get lastPaymentStatus() {
    if (this.employee.contributionPayments[0]?.transaction_date) {
      return this.intl.t('slr.payment_sent');
    } else if (this.employee.contributionPayments[0]?.line_item_date) {
      return this.intl.t('slr.in_progress');
    } else return this.intl.t('slr.pending');
  }

  get isLoading() {
    return (
      this.employee.contributionsState.isLoading ||
      this.employee.futurePayments.isLoading ||
      this.employee.documentsData.isLoading
    );
  }

  get nextContributionPayment() {
    return this.employee.futureContributionPayments[0];
  }

  get nextPaymentAmount() {
    return (this.nextContributionPayment?.amount || 0) / 100;
  }

  get nextPaymentDate() {
    return this.nextContributionPayment?.date || '';
  }

  get nextPaymentStatus() {
    const status = this.nextContributionPayment?.state || '';
    if (status === 'PENDING_APPROVAL') {
      return this.intl.t('slr.pending');
    }
    return status.replace(/_/g, ' ').toLowerCase();
  }

  get totalPayments() {
    return this.employee.contributionSummary?.total / 100 || 0;
  }

  get paymentsCount() {
    return this.employee.contributionPayments?.length || 0;
  }

  get hasContributions() {
    return this.employee.hasContributions;
  }

  get showContributionSelector() {
    return this.person.activeAccounts.length > 1;
  }

  get isMonthlyPlan() {
    return this.sessionContext.currentEmployee?.plan?.cadence === 'MONTHLY';
  }

  get showNextPayment() {
    return (
      this.isMonthlyPlan &&
      this.nextPaymentDate &&
      this.nextPaymentAmount &&
      !this.nextContributionPayment?.validation_errors?.length &&
      this.accountIsValid
    );
  }

  get isSlrEligible() {
    // TODO: Consolidate isSlr checks to the employee service or model
    const cadence = this.sessionContext.currentEmployee?.plan?.cadence;
    return cadence && cadence !== 'NEVER';
  }

  toggleChangeAccountModal = () => {
    this.isOpenAccountModal = !this.isOpenAccountModal;
  };

  navigateToObservabilityUpload = () => {
    this.router.transitionTo('authenticated.observability.upload', {
      queryParams: { provider: 'statement,nslds' },
    });
  };

  toggleGeneratePdfModal = () => {
    this.isGeneratePdfModalOpen = !this.isGeneratePdfModalOpen;
    if (!this.isGeneratePdfModalOpen) {
      this.startDate = getOneYearAgoDate();
      this.endDate = new Date().toISOString().split('T')[0];
      this.pdfUrl = '';
      this.pdfFilename = '';
    }
  };

  toggleAccountModal = () => {
    this.isOpenAccountModal = !this.isOpenAccountModal;
  };

  reloadRecords = () => {
    this.router.refresh('authenticated.slr.dashboard');
  };

  generatePDF = dropTask(async (employee?: EmployeeModel) => {
    if (!employee) {
      console.error('No employee found!');
      return;
    }
    const successMessage = this.intl.t('slr.employer_contributions.pdf.success');
    const errorMessage = this.intl.t('slr.employer_contributions.pdf.error');
    try {
      const response = await fetch(`${ENV.apiHost}/accounting-transactions/generate_pdf`, {
        method: 'POST',
        body: JSON.stringify({
          employee_id: employee.id,
          start_date: this.startDate,
          end_date: this.endDate,
        }),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ENV.apiKey,
          'tio-auth-token': this.sessionContext.session.data.authenticated?.access_token || '',
          'Time-Zone': timeZone(),
          'X-Request-Id': generateRequestId(),
        },
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        console.error('Failed to generate transactions PDF:', errorPayload || response.statusText);
        throw new Error('PDF generation failed');
      }

      this.notifications.add(successMessage, {
        appearance: 'success',
      });

      const payload = await response.json();
      const signedUrl = payload.url;
      const fileName = payload.filename;

      if (!signedUrl || !fileName) {
        throw new Error('Missing signed URL or file name in response');
      }

      this.pdfUrl = signedUrl;
      this.pdfFilename = fileName;
    } catch (error) {
      console.error('Error during PDF generation/download:', error);
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    }
  });

  get paymentValidationError() {
    return this.nextContributionPayment?.validation_errors?.filter((error) =>
      [
        PaymentValidationErrorKey.INVALID_LOAN_TYPE,
        PaymentValidationErrorKey.NO_EMPLOYEE_TRANSACTIONS,
        PaymentValidationErrorKey.EXCEEDED_LIMIT,
      ].includes(error.key)
    )?.[0]?.key;
  }

  get accountIsValid() {
    return (
      !this.isLoading &&
      !!this.person.accountReceivingContributions &&
      !this.employee.accountAlerts.length &&
      !this.paymentValidationError
    );
  }

  <template>
    {{#if this.useReimaginedAccountActivity}}
      {{#if this.reimaginedModel}}
        <ReimaginedContributionHistory @model={{this.reimaginedModel}} />
      {{/if}}
    {{else}}
      <VStack>
        <Header>
          <div class="flex items-center gap-x-4">
            {{t "slr.slr_page_header"}}
            {{#if (and this.isSlrEligible this.accountIsValid)}}
              <div class="flex items-center gap-x-1">
                <span class="text-green-500">
                  <pre>&bull;</pre>
                </span>
                <span class="text-black text-sm font-normal">
                  {{t "slr.receiving_contributions"}}
                </span>
              </div>
            {{/if}}
          </div>
        </Header>
        <Section>
          <:header>
            {{t "slr.employer_contributions.title"}}
          </:header>
          <:actions>
            {{#if this.hasContributions}}
              <Button
                @appearance="outlined"
                @intent="primary"
                {{on "click" this.toggleGeneratePdfModal}}
              >
                {{t "slr.employer_contributions.pdf.generate"}}
              </Button>
            {{/if}}
          </:actions>
          <:body>
            <VStack class="flex flex-col gap-y-6">
              {{#unless this.isSlrEligible}}
                <Alert @intent="warning">
                  <VStack class="pt-0">
                    <span class="font-semibold">{{t "slr.not_eligible.title"}}</span>
                    <p>{{t "slr.not_eligible.message"}}</p>
                  </VStack>
                </Alert>
              {{/unless}}
              {{#if (and this.isSlrEligible this.person.accountReceivingContributions)}}
                <div class="flex items-center gap-x-2">
                  <h3 class="font-medium truncate">
                    {{t "slr.account_receiving_contributions"}}:
                  </h3>
                  <span class="pr-4">
                    {{this.person.accountReceivingContributions.institutionName}}
                  </span>
                  {{#if this.showContributionSelector}}
                    <div class="inline-block border-l border-gray-300 pl-4">
                      <Button
                        @size="sm"
                        @appearance="minimal"
                        @intent="primary"
                        {{on "click" this.toggleChangeAccountModal}}
                      >
                        {{t "slr.change"}}
                      </Button>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
              {{#if this.isSlrEligible}}
                {{#unless this.isLoading}}
                  <SlrAccountAlert
                    @person={{this.person}}
                    @accountAlerts={{this.employee.accountAlerts}}
                    @paymentValidationError={{this.paymentValidationError}}
                    @isPartiallyApplied={{this.employee.isPartiallyApplied}}
                  />
                {{/unless}}
              {{/if}}
              <HStack class="items-center gap-x-12!">
                {{#if this.hasContributions}}
                  <DefinitionList class="divide-none">
                    <:default as |options|>
                      <options.dt class="text-black!">
                        {{t "slr.employer_contributions.last"}}
                        <span class="bg-gray-100 py-0.5 px-3 ml-2 rounded">{{safeFormatDate
                            this.lastPaymentDate
                            month="short"
                            year="numeric"
                          }}</span>
                      </options.dt>
                      <options.dd class="*:text-2xl *:text-purple-900 decoration-purple-900">
                        {{safeFormatNumber this.lastPaymentAmount style="currency" currency="USD"}}
                      </options.dd>
                      <options.dd>
                        <StatusText
                          @status={{this.lastPaymentStatus}}
                          @lastPaymentDate={{this.lastPaymentDate}}
                        />
                      </options.dd>
                    </:default>
                  </DefinitionList>
                {{/if}}
                {{#if this.showNextPayment}}
                  <DefinitionList class="divide-none">
                    <:default as |options|>
                      <options.dt class="text-black!">
                        {{t "slr.employer_contributions.next"}}
                        <span class="bg-gray-100 py-0.5 px-3 ml-2 rounded">
                          {{safeFormatDate this.nextPaymentDate month="short" year="numeric"}}
                        </span>
                      </options.dt>
                      <options.dd class="*:text-2xl *:text-ocean-400 decoration-ocean-400">
                        {{safeFormatNumber this.nextPaymentAmount style="currency" currency="USD"}}
                      </options.dd>
                      <options.dd>
                        <StatusText @status={{this.nextPaymentStatus}} />
                      </options.dd>
                    </:default>
                  </DefinitionList>
                {{/if}}
                {{#if this.paymentsCount}}
                  <DefinitionList class="divide-none">
                    <:default as |options|>
                      <options.dt class="text-black!">
                        {{t "slr.total"}}
                      </options.dt>
                      <options.dd class="*:text-2xl *:text-midnight decoration-midnight">
                        {{safeFormatNumber this.totalPayments style="currency" currency="USD"}}
                      </options.dd>
                      <options.dd>
                        {{#let (safeFormatNumber this.paymentsCount) as |total|}}
                          {{t "slr.employer_contributions.total_contributions" total=total}}
                        {{/let}}
                      </options.dd>
                    </:default>
                  </DefinitionList>
                {{/if}}
              </HStack>
              {{#if this.hasContributions}}
                <Table @contributionPayments={{this.employee.contributionPayments}} />
              {{/if}}
            </VStack>
          </:body>
        </Section>
      </VStack>

      <ChangeActiveAccountModal
        @onClose={{this.toggleAccountModal}}
        @show={{this.isOpenAccountModal}}
        @accountOptions={{this.person.activeAccounts}}
        @accountReceivingContributions={{this.person.accountReceivingContributions}}
        @personId={{this.person.id}}
        @route="authenticated.slr.dashboard"
        @onAccountChanged={{this.reloadRecords}}
      />

      <Modal
        @isOpen={{this.isGeneratePdfModalOpen}}
        @onClose={{this.toggleGeneratePdfModal}}
        class="min-h-64"
        @size="md"
      >
        <:header>{{t "slr.employer_contributions.pdf.info"}}</:header>
        <:body>
          {{#if this.pdfUrl.length}}
            <HStack class="justify-center w-full">
              <Button {{on "click" this.toggleGeneratePdfModal}}>
                {{t "common.close"}}
              </Button>
              <TioDownloadButton @url={{this.pdfUrl}} @filename={{this.pdfFilename}} class="ml-2" />
            </HStack>
          {{else}}
            <VStack>
              <Input
                @label={{t "slr.employer_contributions.pdf.start_date"}}
                @value={{this.startDate}}
                @onInput={{fn (mut this.startDate)}}
                type="date"
              />
              <Input
                @label={{t "slr.employer_contributions.pdf.end_date"}}
                @value={{this.endDate}}
                @onInput={{fn (mut this.endDate)}}
                type="date"
              />
            </VStack>
          {{/if}}
        </:body>
        <:footer>
          {{#unless this.pdfUrl.length}}
            <Footer
              @isRunning={{false}}
              @onCancel={{this.toggleGeneratePdfModal}}
              @onSubmit={{fn this.generatePDF.perform this.person.activeEmployee}}
            />
          {{/unless}}
        </:footer>
      </Modal>
    {{/if}}
  </template>
}

interface SlrAccountAlertSignature {
  Args: {
    person: PersonModel;
    accountAlerts?: { content: string; uploadDocument?: boolean; addressSelect?: boolean }[];
    paymentValidationError?: PaymentValidationErrorKey;
    isPartiallyApplied?: boolean;
  };
}

class SlrAccountAlert extends Component<SlrAccountAlertSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: StoreService;

  @tracked showEditAddress = false;
  @tracked editAddressHasSubmitted = false;
  @tracked selectedAddress?: PostalAddressModel;
  @tracked addressConfirmationChecked = false;

  get accountAlertsCount() {
    return this.args.accountAlerts?.length || 0;
  }

  get allErrorsRequireUpload() {
    return (
      (this.args.accountAlerts?.length || 0) > 1 &&
      this.args.accountAlerts?.every((error) => error.uploadDocument)
    );
  }

  get isAddressSubmitDisabled() {
    return (
      !this.selectedAddress || this.editAddressHasSubmitted || !this.addressConfirmationChecked
    );
  }

  get selectedAddressId() {
    return this.selectedAddress?.id || '';
  }

  toggleEditAddress = () => {
    this.showEditAddress = !this.showEditAddress;
    this.selectedAddress = undefined;
  };

  setSelectedAddress = (id: string | null) => {
    this.selectedAddress = this.addressOptions.find((a) => a.key === id)?.address;
  };

  get addressOptions() {
    return (
      // @ts-expect-error
      this.addressData.value?.map((address: PostalAddressModel) => ({
        label: htmlSafe(
          address.addressLine1 +
            (address.addressLine2 ? '\n ' + address.addressLine2 : '') +
            (address.addressLine3 ? '\n ' + address.addressLine3 : '')
        ),
        key: address.id,
        address,
      })) || []
    );
  }

  addressData = trackedFunction(this, async () => {
    // @ts-expect-error
    return await this.store.query('postal-address', {
      filter: {
        financialInstitution:
          this.args.person.accountReceivingContributions?.financialInstitution?.id,
        isActive: true,
        type: 'PAYMENT',
      },
      sort: 'address-line1',
      page: {
        offset: 0,
        limit: 400,
      },
    });
  });

  updateAddress = async () => {
    this.updateAddressTask.perform();
  };

  updateAddressTask = dropTask(async () => {
    this.editAddressHasSubmitted = true;
    const account = this.args.person.accountReceivingContributions;
    if (!this.selectedAddress || !account) {
      return;
    }
    try {
      const newAddress = this.addressData.value?.find(
        // @ts-expect-error
        (a: PostalAddressModel) => a.id === this.selectedAddress?.id
      );
      if (!newAddress) {
        throw new Error('Address not found');
      }
      account.postalAddress = newAddress as PostalAddressModel;
      await account.save();
      this.editAddressHasSubmitted = false;
      this.toggleEditAddress();
      const successMessage = this.intl.t('common.accounts.update_address.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMessage = this.intl.t('common.accounts.update_address.error');
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    }
  });

  <template>
    {{#if (eq @paymentValidationError "EXCEEDED_LIMIT")}}
      <Alert @intent="warning">
        <VStack class="pt-0">
          <span class="font-medium">{{t "slr.error.EXCEEDED_LIMIT"}}</span>
          <p class="text-sm">{{t "slr.error.contact_support" htmlSafe=true}}</p>
        </VStack>
      </Alert>
    {{else if @isPartiallyApplied}}
      <Alert @intent="warning">
        <VStack class="pt-0">
          <span class="font-medium">{{t "slr.error.account_needs_to_be_verified"}}</span>
          <p>{{t "slr.error.account_needs_to_be_verified_info"}}</p>
          <p class="text-sm">{{t "slr.error.contact_support" htmlSafe=true}}</p>
        </VStack>
      </Alert>
    {{else if (not @person.activeAccounts.length)}}
      <AppAlertNoLinkedLoans @person={{@person}} @product="slr" />
    {{else if this.accountAlertsCount}}
      <Alert @intent="warning">
        <VStack class="pt-0">
          <span class="font-medium">{{t "slr.error.loan_account_issue"}}</span>
          <p>{{t "slr.error.missing_information"}}</p>
          <ul class="list-disc px-6">
            {{#each @accountAlerts as |error|}}
              <li>
                {{htmlSafe error.content}}
                {{#unless this.allErrorsRequireUpload}}
                  {{#if error.uploadDocument}}
                    <LinkTo
                      @route="authenticated.observability.upload"
                      @query={{hash source="slr" providers="statement,nslds"}}
                      class="underline font-semibold float-right"
                    >
                      {{t "slr.upload_latest_statement"}}
                    </LinkTo>
                  {{else if error.addressSelect}}
                    <Button
                      {{on "click" this.toggleEditAddress}}
                      @appearance="minimal"
                      class="underline float-right"
                    >
                      {{t "slr.select_address"}}
                    </Button>
                  {{/if}}
                {{/unless}}
              </li>
            {{/each}}
            {{#if this.allErrorsRequireUpload}}
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash source="slr" providers="statement,nslds"}}
                class="underline font-semibold"
              >
                {{t "slr.upload_latest_statement"}}
              </LinkTo>
            {{/if}}
          </ul>
        </VStack>
      </Alert>
    {{else if @paymentValidationError}}
      <Alert @intent="warning">
        <VStack class="pt-0">
          <p>{{t (concat "slr.error." @paymentValidationError)}}</p>
          {{#if (eq @paymentValidationError "INVALID_LOAN_TYPE")}}
            <div>
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash source="slr" providers="statement,nslds"}}
                class="underline"
              >
                {{t "slr.upload_latest_statement"}}
              </LinkTo>
            </div>
          {{/if}}
          <p class="text-sm">{{t "slr.error.contact_support" htmlSafe=true}}</p>
        </VStack>
      </Alert>
    {{/if}}

    <Modal @isOpen={{this.showEditAddress}} @onClose={{this.toggleEditAddress}} @size="xl">
      <:header>{{t "common.accounts.select_payment_address"}}</:header>
      <:body>
        <VStack class="p-0!">
          <p>{{t "slr.select_address_body_1"}}</p>
          <p class="italic text-gray-500">{{t "slr.select_address_body_2"}}</p>
          <form class="flex flex-col gap-y-4">
            <Select
              @searchable={{true}}
              @clearable={{true}}
              @items={{this.addressOptions}}
              @selected={{this.selectedAddressId}}
              @onChange={{this.setSelectedAddress}}
              @label="Servicer Address"
            />
            <Checkbox
              disabled={{this.updateAddressTask.isRunning}}
              @label="I confirm the selected payment address is correct and understand that incorrect addresses may cause payment to be sent to the wrong address and delays of 90 days or longer."
              @checked={{this.addressConfirmationChecked}}
              @onChange={{fn (mut this.addressConfirmationChecked)}}
              @classes={{hash base="items-start" labelContainer="-mt-1"}}
            />
          </form>
        </VStack>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.updateAddressTask.isRunning}}
          @onCancel={{this.toggleEditAddress}}
          @onSubmit={{this.updateAddress}}
          @submitDisabled={{this.isAddressSubmitDisabled}}
        />
      </:footer>
    </Modal>
  </template>
}
